import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box } from '@material-ui/core';
import {
  useAlert,
  useIfMounted,
  useOnChange,
  useOnPendingActionFinish,
  useOnUnmount,
} from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';
import { AsyncUtils } from '../../../common/utils';
import { EmptyState, Panel } from '../../../common/components';
import { selectPendingActions } from '../../../common/state/pending';
import {
  selectChannels,
  selectProducts,
  selectSubscriptionDetails,
  selectSubscriptions,
} from '../state/subscriptionSelectors';
import {
  fetchSubscriptions,
  fetchSubscription,
  resetCreditCard,
  closeSubscription,
  reactivateSubscription,
  changeSubscription,
  createSupportProduct,
  deselectSubscription,
  invalidateCache,
  removePaymentBlock,
  removeUserBlock,
  addSubscriptionDiscount,
  fetchDiscountReasons,
} from '../state/subscriptionActions';
import { createPartnerSubscription, closePartnerSubscription } from '../state/subscriptionActions';
import { selectCustomerDetails } from '../../customers/state/customerSelectors';
import { CANCEL_ORDER, REFUND_ORDER } from '../../orders/state/orderActionTypes';
import SubscriptionList from './partials/list/SubscriptionList';
import SubscriptionDetails from './partials/details/SubscriptionDetails';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ProductService from '../services/ProductService';
import SubscriptionService from '../services/SubscriptionService';
import { getProductName } from '../constants';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    minHeight: '500px',
  },
}));

function SubscriptionsPage() {
  const [showExpired, setShowExpired] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [t] = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { showAlert } = useAlert();
  const classes = useStyles();
  const pendingActions = useSelector(selectPendingActions);
  const channels = useSelector(selectChannels);
  const products = useSelector(selectProducts);
  const subscriptions = useSelector(selectSubscriptions);
  const subscriptionDetails = useSelector(selectSubscriptionDetails);
  const customerDetails = useSelector(selectCustomerDetails);
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();
  
  const useResendAddonActivationLink = (userId: string) => {
    return useMutation({
      mutationFn: (productId: string) => 
        ProductService.resendAddonActivationLink(userId, productId),
    });
  };

  const useUnlinkAddons = (userId: string) => {
    return useMutation({
      mutationFn: (productId: string) => 
        ProductService.unlinkAddons(userId, productId),
    });
  };

  const resendLink = useResendAddonActivationLink(userId);
  const unlinkAddons = useUnlinkAddons(userId);
  const queryClient = useQueryClient();

  const subscriptionDetailsQueryKey = useMemo(
    () => ['subscription', userId, subscriptionDetails?.id],
    [userId, subscriptionDetails?.id]
  );

  const { data: subscriptionDetailsFromQuery } = useQuery({
    queryKey: subscriptionDetailsQueryKey,
    queryFn: () => SubscriptionService.fetchSubscription(userId, subscriptionDetails.id).then(r => r.data),
    enabled: !!subscriptionDetails?.id,
    initialData: subscriptionDetails,
  });

  useOnChange(subscriptionDetails, (nextSubscriptionDetails: any) => {
     if (!nextSubscriptionDetails?.id) {
       return;
     }
     queryClient.setQueryData(
       ['subscription', userId, nextSubscriptionDetails.id],
       nextSubscriptionDetails
     );
   });

  const handleFetchSubscriptions = useCallback(
    async page => {
      try {
        await dispatch(fetchSubscriptions(userId, showExpired, page));
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, showExpired, showAlert, t, userId]
  );

  const handleSelectSubscription = useCallback(
    async params => {
      try {
        await dispatch(fetchSubscription(userId, params.row.id));
        ifMounted(() => setShowDetails(true));
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, ifMounted, showAlert, t, userId]
  );

  const handleResetCreditCard = useCallback(async () => {
    try {
      await dispatch(resetCreditCard(subscriptionDetails.paymentInfo.pic));
      showAlert({
        title: t('common.success'),
        message: t('subscription.subscriptionResetCreditCardSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('subscription.subscriptionResetCreditCardError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, subscriptionDetails, t]);

  const handleCloseSubscription = useCallback(
    async force => {
      try {
        await dispatch(closeSubscription(userId, subscriptionDetails.id, force));
        await dispatch(fetchSubscriptions(userId, showExpired, subscriptions?.page));
        showAlert({
          title: t('common.success'),
          message: t('subscription.subscriptionCloseSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionCloseError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, showAlert, showExpired, subscriptionDetails, subscriptions, t, userId]
  );

  const handleClosePartnerSubscription = useCallback(
    async vasToken => {
      try {
        await dispatch(closePartnerSubscription(vasToken));
        await dispatch(fetchSubscriptions(userId, showExpired, subscriptions?.page));
        showAlert({
          title: t('common.success'),
          message: t('subscription.subscriptionCloseSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionCloseError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, showAlert, showExpired, subscriptions, t, userId]
  );

  const handleReactivateSubscription = useCallback(async () => {
    try {
      await dispatch(reactivateSubscription(userId, subscriptionDetails.products));
      await AsyncUtils.executeAll([
        dispatch(fetchSubscription(userId, subscriptionDetails.id)),
        dispatch(fetchSubscriptions(userId, showExpired, subscriptions?.page)),
      ]);
      showAlert({
        title: t('common.success'),
        message: t('subscription.subscriptionReactivateSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('subscription.subscriptionReactivateError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, showExpired, subscriptionDetails, subscriptions, t, userId]);

  const handleChangeSubscription = useCallback(
    async ({ product, override }) => {
      try {
        await dispatch(
          changeSubscription(
            userId,
            subscriptionDetails.id,
            subscriptionDetails.productId,
            product,
            override
          )
        );
        await dispatch(fetchSubscriptions(userId, showExpired, subscriptions?.page));
        showAlert({
          title: t('common.success'),
          message: t('subscription.subscriptionChangeSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionChangeError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, showExpired, subscriptionDetails, subscriptions, t, userId]
  );

  const handleCreateSupportProduct = useCallback(
    async ({ product }) => {
      try {
        if (product.partnerAmbassador)
          await dispatch(createPartnerSubscription(customerDetails.email, product.data.id));
        else await dispatch(createSupportProduct(userId, product.data));
        await dispatch(fetchSubscriptions(userId, showExpired, subscriptions?.page));
        showAlert({
          title: t('common.success'),
          message: t('subscription.subscriptionAssignSupportProductSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e: any) {
        showAlert({
          title: t('subscription.subscriptionAssignSupportProductError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, showExpired, subscriptions, t, userId, customerDetails]
  );

  const handleInvalidateCache = useCallback(async () => {
    try {
      await dispatch(invalidateCache(userId));
      showAlert({
        title: t('common.success'),
        message: t('common.success'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, userId, t]);

  const handleRemovePaymentBlock = useCallback(async pic => {
    try {
      await dispatch(removePaymentBlock(pic));
      await dispatch(fetchSubscription(userId, subscriptionDetails.id));
      showAlert({
        title: t('common.success'),
        message: t('common.success'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, userId, subscriptionDetails, t]);

  const handleRemoveUserBlock = useCallback(async () => {
    try {
      await dispatch(removeUserBlock(userId));
      await dispatch(fetchSubscription(userId, subscriptionDetails.id));
      showAlert({
        title: t('common.success'),
        message: t('common.success'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, userId, subscriptionDetails, t]);

  const handleFetchDiscountReasons = useCallback(async () => {
    try {
      await dispatch(fetchDiscountReasons());
    } catch (e: any) {
      showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, t]);

  const handleAddSubscriptionDiscount = useCallback(async discount => {
    try {
      await dispatch(addSubscriptionDiscount(userId, subscriptionDetails.id, discount));
      showAlert({
        title: t('common.success'),
        message: t('subscription.subscriptionAddDiscountSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e: any) {
      showAlert({
        title: t('subscription.subscriptionAddDiscountError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, showAlert, userId, subscriptionDetails, t]);

  const handleUnlinkAddonSubscription = useCallback(async (productId) => {
    unlinkAddons.mutate(productId, {
      onSuccess: () => {
        queryClient.invalidateQueries(subscriptionDetailsQueryKey);
        showAlert({
          title: t('common.success'),
          message: t('subscription.subscriptionUnlinkAddonSuccess', { productName: getProductName(productId) }),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: any) => showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      }),
    });
  }, [unlinkAddons, queryClient, subscriptionDetailsQueryKey, showAlert, t]);

  const handleResendAddonActivationLink = useCallback(async (productId) => {
    resendLink.mutate(productId, {
      onSuccess: () => showAlert({
        title: t('common.success'),
        message: t('subscription.subscriptionResendAddonActivationLinkSuccess', { productName: getProductName(productId) }),
        severity: AlertSeverity.Success,
      }),
      onError: (e: any) => showAlert({
        title: t('common.error'),
        message: e.message,
        severity: AlertSeverity.Error,
      }),
    });
  }, [resendLink, showAlert, t]);

  useOnUnmount(() => {
    if (subscriptionDetails) {
      dispatch(deselectSubscription(undefined, undefined));
    }
  });

  useOnChange(showDetails, () => {
    if (!showDetails) {
      dispatch(deselectSubscription(undefined, undefined));
    }
  });

  useOnChange(showExpired, () => {
    handleFetchSubscriptions(1);
  });

  useOnPendingActionFinish([REFUND_ORDER, CANCEL_ORDER], () => {
    handleFetchSubscriptions(subscriptions?.page);
  });

  return (
    <Box id="Subscriptions-container" className={classes.container}>
      <Panel id="Subscriptions-panel" variant="contained">
        {!subscriptions ? (
          <EmptyState variant="unavailable" message={t('subscription.subscriptionsNotAvailable')} />
        ) : (
          <>
            {showDetails && subscriptionDetails ? (
              <SubscriptionDetails
                subscription={subscriptionDetailsFromQuery ?? subscriptionDetails}
                channels={_.defaultTo(channels, [])}
                products={_.defaultTo(products, [])}
                onResetCreditCard={handleResetCreditCard}
                onCloseSubscription={handleCloseSubscription}
                onClosePartnerSubscription={handleClosePartnerSubscription}
                onReactivateSubscription={handleReactivateSubscription}
                onChangeSubscription={handleChangeSubscription}
                onRemovePaymentBlock={handleRemovePaymentBlock}
                onRemoveUserBlock={handleRemoveUserBlock}
                onHideDetails={() => setShowDetails(false)}
                actionsDisabled={!_.isEmpty(pendingActions)}
                onFetchDiscountReasons={handleFetchDiscountReasons}
                onAddSubscriptionDiscount={handleAddSubscriptionDiscount}
                onResendAddonActivationLink={handleResendAddonActivationLink}
                onUnlinkAddonSubscription={handleUnlinkAddonSubscription}
              />
            ) : (
              <SubscriptionList
                showExpired={showExpired}
                data={subscriptions}
                channels={_.defaultTo(channels, [])}
                products={_.defaultTo(products, [])}
                onFetchData={handleFetchSubscriptions}
                onSelectSubscription={handleSelectSubscription}
                onCreateSupportProduct={handleCreateSupportProduct}
                onFilterChange={() => setShowExpired(!showExpired)}
                actionsDisabled={!_.isEmpty(pendingActions)}
                onInvalidateCache={handleInvalidateCache}
              />
            )}
          </>
        )}
      </Panel>
    </Box>
  );
}

export default SubscriptionsPage;
